<template>
    <div class="image-editor-overlay">
        <div class="image-editor-container">
            <div class="header">
                <h3>Resim Düzenleyici</h3>
                <div class="toolbar">
                    <div class="tool-group">
                        <div class="tool-wrapper">
                            <a-tooltip :title="`Çiz (${shortcuts.draw})`">
                                <a-button
                                    class="tool-btn"
                                    :type="mode === 'draw' ? 'primary' : 'default'"
                                    @click="toggleDrawMode"
                                >
                                    <i class="material-icons">brush</i>
                                </a-button>
                            </a-tooltip>
                            <span class="shortcut-hint">{{ shortcuts.draw }}</span>
                        </div>

                        <div v-if="mode === 'draw'" class="draw-options">
                            <a-popover
                                placement="bottom"
                                trigger="click"
                                v-model="colorPickerVisible"
                            >
                                <template slot="content">
                                    <div class="color-picker">
                                        <div
                                            v-for="color in colors"
                                            :key="color"
                                            class="color-option"
                                            :style="{
                                                backgroundColor: color,
                                                border: drawColor === color
                                                    ? '2px solid #1890ff'
                                                    : '1px solid #e8e8e8'
                                            }"
                                            @click="selectColor(color)"
                                        ></div>
                                    </div>
                                </template>
                                <a-button
                                    class="color-btn"
                                    :style="{ backgroundColor: drawColor }"
                                ></a-button>
                            </a-popover>

                            <a-slider
                                v-model="drawWidth"
                                :min="1"
                                :max="20"
                                :step="1"
                                class="width-slider"
                            />
                        </div>
                    </div>

                    <div class="tool-group">
                        <div class="tool-wrapper">
                            <a-tooltip :title="`Metin (${shortcuts.text})`">
                                <a-button
                                    class="tool-btn"
                                    :type="mode === 'text' ? 'primary' : 'default'"
                                    @click="toggleTextMode"
                                >
                                    <i class="material-icons">title</i>
                                </a-button>
                            </a-tooltip>
                            <span class="shortcut-hint">{{ shortcuts.text }}</span>
                        </div>

                        <div v-if="mode === 'text'" class="text-options draw-options">
                            <a-popover
                                placement="bottom"
                                trigger="click"
                                v-model="textColorPickerVisible"
                            >
                                <template slot="content">
                                    <div class="color-picker">
                                        <div
                                            v-for="color in colors"
                                            :key="color"
                                            class="color-option"
                                            :style="{
                                                backgroundColor: color,
                                                border: textColor === color
                                                    ? '2px solid #1890ff'
                                                    : '1px solid #e8e8e8'
                                            }"
                                            @click="selectTextColor(color)"
                                        ></div>
                                    </div>
                                </template>
                                <a-button
                                    class="color-btn"
                                    :style="{ backgroundColor: textColor }"
                                ></a-button>
                            </a-popover>
                            <a-slider
                                v-model="fontSize"
                                :min="10"
                                :max="60"
                                :step="1"
                                class="width-slider"
                            />
                        </div>
                    </div>

                    <div class="tool-group">
                        <div class="tool-wrapper">
                            <a-tooltip :title="`Sola Döndür (${shortcuts.rotateLeft})`">
                                <a-button class="tool-btn" @click="rotateLeft">
                                    <i class="material-icons">rotate_left</i>
                                </a-button>
                            </a-tooltip>
                            <span class="shortcut-hint">{{ shortcuts.rotateLeft }}</span>
                        </div>
                        <div class="tool-wrapper">
                            <a-tooltip :title="`Sağa Döndür (${shortcuts.rotateRight})`">
                                <a-button class="tool-btn" @click="rotateRight">
                                    <i class="material-icons">rotate_right</i>
                                </a-button>
                            </a-tooltip>
                            <span class="shortcut-hint">{{ shortcuts.rotateRight }}</span>
                        </div>
                    </div>
                    <div class="tool-group">
                        <div class="tool-wrapper">
                            <a-tooltip :title="`Yakınlaştır (${shortcuts.zoomIn})`">
                                <a-button class="tool-btn" @click="zoomIn">
                                    <i class="material-icons">zoom_in</i>
                                </a-button>
                            </a-tooltip>
                            <span class="shortcut-hint">{{ shortcuts.zoomIn }}</span>
                        </div>
                        <div class="tool-wrapper">
                            <a-tooltip :title="`Uzaklaştır (${shortcuts.zoomOut})`">
                                <a-button class="tool-btn" @click="zoomOut">
                                    <i class="material-icons">zoom_out</i>
                                </a-button>
                            </a-tooltip>
                            <span class="shortcut-hint">{{ shortcuts.zoomOut }}</span>
                        </div>
                    </div>
                    <div class="tool-group">
                        <div class="tool-wrapper">
                            <a-tooltip :title="`Sıfırla (${shortcuts.reset})`">
                                <a-button class="tool-btn" @click="resetImage">
                                    <i class="material-icons">refresh</i>
                                </a-button>
                            </a-tooltip>
                            <span class="shortcut-hint">{{ shortcuts.reset }}</span>
                        </div>
                    </div>
                    <div class="tool-group">
                        <div class="tool-wrapper">
                            <a-tooltip :title="`Geri Al (${shortcuts.undo})`">
                                <a-button
                                    class="tool-btn"
                                    @click="undo"
                                    :disabled="!canUndo"
                                >
                                    <i class="material-icons">undo</i>
                                </a-button>
                            </a-tooltip>
                            <span class="shortcut-hint">{{ shortcuts.undo }}</span>
                        </div>
                        <div class="tool-wrapper">
                            <a-tooltip :title="`İleri Al (${shortcuts.redo})`">
                                <a-button
                                    class="tool-btn"
                                    @click="redo"
                                    :disabled="!canRedo"
                                >
                                    <i class="material-icons">redo</i>
                                </a-button>
                            </a-tooltip>
                            <span class="shortcut-hint">{{ shortcuts.redo }}</span>
                        </div>
                    </div>
                </div>
                <a-button type="link" @click="$emit('close')">
                    <i class="material-icons">close</i>
                </a-button>
            </div>
            <div class="content">
                <div class="editor-container">
                    <canvas
                        ref="editorCanvas"
                        :width="canvasWidth"
                        :height="canvasHeight"
                        class="canvas-style"
                        @mousedown="onMouseDown"
                        @mousemove="onMouseMove"
                        @mouseup="onMouseUp"
                        @mouseleave="onMouseLeave"
                        @dblclick="onDblClick"
                    ></canvas>
                </div>

                <div class="thumbnail-container">
                    <div class="caption-section" v-if="images[currentEditIndex]">
                        <a-input
                            v-model="images[currentEditIndex].caption"
                            placeholder="Seçili resme açıklama ekleyin..."
                            class="caption-input"
                        />
                    </div>
                    <div class="thumbnail-list">
                        <div
                            v-for="(image, index) in images"
                            :key="index"
                            class="thumbnail-item"
                            :class="{ active: index === currentEditIndex }"
                            @click="changeImage(index)"
                        >
                            <img :src="image.preview" :alt="image.file.name" />

                            <div class="thumbnail-actions">
                                <a-button
                                    type="danger"
                                    shape="circle"
                                    size="small"
                                    class="delete-btn"
                                    @click.stop="removeImage(index)"
                                >
                                    <i class="material-icons">delete</i>
                                </a-button>
                            </div>
                        </div>

                        <div class="thumbnail-item add-new" @click="triggerAddImage">
                            <i class="material-icons">add_photo_alternate</i>
                            <span>Yeni Resim Ekle</span>
                            <input
                                type="file"
                                ref="addImageRef"
                                multiple
                                style="display: none"
                                accept="image/*"
                                @change="onNewImagesSelected"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="footer">
                <a-button @click="$emit('close')">İptal</a-button>
                <a-button type="primary" @click="handleSave">Kaydet</a-button>
            </div>
            <TextEditModal
                v-if="editingText"
                :visible="editingText !== null"
                :text="editingText"
                :colors="colors"
                @save="(updatedText) => {
                    editingText.text = updatedText.text;
                    editingText.color = updatedText.color;
                    editingText.size = updatedText.size;
                    editingText = null;
                    redrawAll();
                    saveState();
                }"
                @cancel="() => {
                    editingText = null;
                    redrawAll();
                }"
                @redraw="redrawAll"
            />
        </div>
    </div>
</template>

<script>
import TextEditModal from './TextEditModal.vue';

export default {
    name: "ImageEditor",
    components: {
        TextEditModal
    },
    props: {
        files: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            images: [],
            currentEditIndex: 0,
            canvasWidth: 700,
            canvasHeight: 500,
            context: null,
            drawLayerCanvas: null,
            drawLayerContext: null,
            isDrawing: false,
            mode: "none", // "draw" veya "text" veya "none"
            lastX: 0,
            lastY: 0,
            rotationAngle: 0,
            drawColor: "#000000",
            drawWidth: 2,
            colors: [
                "#000000",
                "#434343",
                "#666666",
                "#999999",
                "#b7b7b7",
                "#cccccc",
                "#d9d9d9",
                "#ffffff",
                "#ff0000",
                "#ff4d4d",
                "#ff9999",
                "#ffcccc",
                "#ff1a1a",
                "#cc0000",
                "#990000",
                "#660000",
                "#00ff00",
                "#4dff4d",
                "#99ff99",
                "#ccffcc",
                "#1aff1a",
                "#00cc00",
                "#009900",
                "#006600",
                "#0000ff",
                "#4d4dff",
                "#9999ff",
                "#ccccff",
                "#1a1aff",
                "#0000cc",
                "#000099",
                "#000066",
                "#ffff00",
                "#ffff4d",
                "#ffff99",
                "#ffffcc",
                "#ffff1a",
                "#cccc00",
                "#999900",
                "#666600",
                "#ff00ff",
                "#ff4dff",
                "#ff99ff",
                "#ffccff",
                "#ff1aff",
                "#cc00cc",
                "#990099",
                "#660066",
                "#00ffff",
                "#4dffff",
                "#99ffff",
                "#ccffff",
                "#1affff",
                "#00cccc",
                "#009999",
                "#006666"
            ],
            texts: [],
            selectedTextId: null,
            textColor: "#000000",
            fontSize: 20,
            tempEditText: "",
            // Bu geçici değerler anlık değişiklikleri göstermek için kullanılır
            tempEditColor: "#000000",
            tempEditSize: 20,
            currentImage: null,
            zoomLevel: 1,
            history: [],
            currentHistoryIndex: -1,
            colorPickerVisible: false,
            textColorPickerVisible: false,
            shortcuts: {
                draw: "B",
                text: "T",
                rotateLeft: "←",
                rotateRight: "→",
                zoomIn: "+",
                zoomOut: "-",
                reset: "R",
                undo: "Ctrl+Z",
                redo: "Ctrl+Y"
            },
            editingText: null
        };
    },
    computed: {
        canUndo() {
            return this.currentHistoryIndex > 0;
        },
        canRedo() {
            return this.currentHistoryIndex < this.history.length - 1;
        }
    },
    watch: {
        currentEditIndex(newIndex, oldIndex) {
            if (oldIndex !== null) {
                this.saveState();
            }
        },
        editingText(newVal) {
            if (newVal === null) {
                this.selectedTextId = null;
            }
        }
    },
    created() {
        this.initializeImages();
    },
    mounted() {
        const mainCanvas = this.$refs.editorCanvas;
        if (mainCanvas) {
            this.context = mainCanvas.getContext("2d");

            this.drawLayerCanvas = document.createElement("canvas");
            this.drawLayerCanvas.width = this.canvasWidth;
            this.drawLayerCanvas.height = this.canvasHeight;
            this.drawLayerContext = this.drawLayerCanvas.getContext("2d");

            this.loadImageToCanvas(0);
        }
        window.addEventListener("keydown", this.handleKeyPress);
    },
    methods: {
        initializeImages() {
            var self = this;
            var imagePromises = this.files.map(function(file, idx) {
                return new Promise(function(resolve) {
                    var previewUrl = URL.createObjectURL(file);
                    var tempImage = new Image();
                    var imageData = {
                        file: file,
                        preview: previewUrl,
                        caption: "",
                        edited: null,
                        originalWidth: 0,
                        originalHeight: 0
                    };
                    tempImage.onload = function() {
                        imageData.originalWidth = tempImage.width;
                        imageData.originalHeight = tempImage.height;
                        self.$set(self.images, idx, imageData);
                        resolve();
                    };
                    tempImage.onerror = function(err) {
                        self.$set(self.images, idx, imageData);
                        resolve();
                    };
                    tempImage.src = previewUrl;
                });
            });

            Promise.all(imagePromises).then(function() {
                if (self.images.length > 0) {
                    self.loadImageToCanvas(0);
                }
            });
        },
        loadImageToCanvas(index) {
            var image = this.images[index];
            var mainCanvas = this.$refs.editorCanvas;
            if (!image || !mainCanvas) {
                console.warn("Geçersiz resim veya canvas referansı", index, image, mainCanvas);
                return;
            }

            var w = image.originalWidth;
            var h = image.originalHeight;
            if (!w || !h) {
                console.warn("Orijinal resim boyutu 0x0. Varsayılan boyut 500x500 kullanılacak.");
                w = 500;
                h = 500;
            }

            this.canvasWidth = w;
            this.canvasHeight = h;

            mainCanvas.width = this.canvasWidth;
            mainCanvas.height = this.canvasHeight;
            this.context = mainCanvas.getContext("2d");

            this.drawLayerCanvas.width = this.canvasWidth;
            this.drawLayerCanvas.height = this.canvasHeight;
            this.drawLayerContext = this.drawLayerCanvas.getContext("2d");
            this.drawLayerContext.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

            var tempImage = new Image();
            var self = this;
            tempImage.onload = function() {
                self.context.clearRect(0, 0, self.canvasWidth, self.canvasHeight);
                self.context.drawImage(tempImage, 0, 0, self.canvasWidth, self.canvasHeight);
                self.currentImage = tempImage;
            };
            tempImage.onerror = function(err) {
                console.error("Resim yüklenemedi.", err);
            };
            tempImage.src = image.preview;
        },
        async changeImage(index) {
            if (index === this.currentEditIndex) {
                return;
            }
            await this.saveCurrentEdit();
            this.currentEditIndex = index;
            this.texts = [];
            this.loadImageToCanvas(index);
        },
        async handleSave() {
            await this.saveCurrentEdit();

            const processedImages = this.images.map(function(image) {
                return {
                    file: image.edited || image.file,
                    caption: image.caption
                };
            });

            this.$emit("save", processedImages);
            this.$emit("close");
        },
        async saveCurrentEdit() {
            if (this.currentEditIndex === null || !this.$refs.editorCanvas) {
                return;
            }
            try {
                const canvas = this.$refs.editorCanvas;
                // Orijinal dosya tipini alıyoruz
                const originalMimeType = this.images[this.currentEditIndex].file.type;
                let targetMimeType = 'image/png';
                let dataURL;

                // Eğer orijinal görüntü JPEG ise canvas'ı JPEG formatında kaydet
                if (originalMimeType === 'image/jpeg' || originalMimeType === 'image/jpg') {
                    // Sıkıştırma kalitesini (ikinci parametre) ihtiyaca göre değiştirebilirsiniz
                    dataURL = canvas.toDataURL('image/jpeg', 0.8);
                    targetMimeType = 'image/jpeg';
                } else {
                    // PNG vb. diğer türler için (kalite paramETresi desteklenmez)
                    dataURL = canvas.toDataURL('image/png');
                }

                const response = await fetch(dataURL);
                const blob = await response.blob();
                // Aynı dosya adını kullanıyoruz ama tipi (MIME) orijinal formata göre güncelledik
                const fileName = this.images[this.currentEditIndex].file.name;
                const newFile = new File([blob], fileName, { type: targetMimeType });

                this.images[this.currentEditIndex].edited = newFile;
                this.images[this.currentEditIndex].preview = URL.createObjectURL(newFile);
            } catch (error) {
                console.error("Error saving edited image:", error);
                this.$message.error("Bir hata oluştu, lütfen tekrar deneyin.");
            }
            this.saveState();
        },
        setMode(newMode) {
            this.mode = newMode;
        },
        // Metin aracını açıp kapatır
        toggleTextMode() {
            this.mode = this.mode === "text" ? "none" : "text";
        },
        // Fırça aracını açıp kapatır
        toggleDrawMode() {
            this.mode = this.mode === "draw" ? "none" : "draw";
        },
        // Canvas'a tıklandığında text modunda ise anında yeni metin ekle
        onMouseDown(event) {
            if (this.mode === "draw") {
                this.isDrawing = true;
                var mousePos = this.getMousePos(event);
                this.lastX = mousePos.x;
                this.lastY = mousePos.y;
            } else if (this.mode === "text") {
                var mousePos = this.getMousePos(event);
                const newText = {
                    id: Date.now(),
                    text: "Metin",
                    x: mousePos.x,
                    y: mousePos.y,
                    isDragging: false,
                    color: this.textColor,
                    size: this.fontSize
                };
                this.texts.push(newText);
                this.selectedTextId = newText.id;
                this.redrawAll();
                this.saveState();

                this.mode = 'none';
                this.$nextTick(() => {
                    this.editSelectedText();
                });
            } else {
                // Diğer modlarda bir şey yapma
                var mousePos = this.getMousePos(event);

                // Var olan bir metin üzerine tıklayıp tıklamadığımızı kontrol edelim
                const clickedText = this.texts.find(
                    function(text) {
                        const metrics = this.context.measureText(text.text);
                        const textWidth = metrics.width;
                        const textHeight = text.size;

                        return (
                            mousePos.x >= text.x - 5 &&
                            mousePos.x <= text.x + textWidth + 5 &&
                            mousePos.y >= text.y - textHeight &&
                            mousePos.y <= text.y + 5
                        );
                    }.bind(this)
                );

                if (clickedText) {
                    this.selectedTextId = clickedText.id;
                    clickedText.isDragging = true;
                    this.lastX = mousePos.x - clickedText.x;
                    this.lastY = mousePos.y - clickedText.y;
                } else {
                    this.selectedTextId = null;
                }
                this.redrawAll();
            }
        },
        onMouseMove(event) {
            if (this.mode === "draw") {
                if (!this.isDrawing || !this.drawLayerContext) return;

                var mousePos = this.getMousePos(event);
                var newX = mousePos.x;
                var newY = mousePos.y;

                this.drawLayerContext.strokeStyle = this.drawColor;
                this.drawLayerContext.lineWidth = this.drawWidth;
                this.drawLayerContext.lineCap = "round";

                this.drawLayerContext.beginPath();
                this.drawLayerContext.moveTo(this.lastX, this.lastY);
                this.drawLayerContext.lineTo(newX, newY);
                this.drawLayerContext.stroke();

                this.lastX = newX;
                this.lastY = newY;

                this.redrawAll();
            } else {
                // Metin sürükleme
                const draggedText = this.texts.find(function(t) {
                    return t.isDragging;
                });
                if (draggedText) {
                    var mousePos = this.getMousePos(event);
                    draggedText.x = mousePos.x - this.lastX;
                    draggedText.y = mousePos.y - this.lastY;
                    this.redrawAll();
                }
            }
        },
        onMouseUp() {
            this.isDrawing = false;
            for (let i = 0; i < this.texts.length; i++) {
                this.texts[i].isDragging = false;
            }
            this.saveState();
        },
        onMouseLeave() {
            this.isDrawing = false;
            for (let i = 0; i < this.texts.length; i++) {
                this.texts[i].isDragging = false;
            }
        },
        // Yeni yolları ve metinleri tekrar çiz
        redrawAll() {
            if (!this.context || !this.currentImage) return;

            this.context.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

            this.context.save();
            this.context.translate(this.canvasWidth / 2, this.canvasHeight / 2);
            this.context.rotate((this.rotationAngle * Math.PI) / 180);
            this.context.drawImage(
                this.currentImage,
                -this.canvasWidth / 2,
                -this.canvasHeight / 2,
                this.canvasWidth,
                this.canvasHeight
            );
            this.context.restore();

            this.context.drawImage(this.drawLayerCanvas, 0, 0);
            this.redrawCanvas();
        },
        redrawCanvas() {
            if (!this.context) return;

            for (let i = 0; i < this.texts.length; i++) {
                const textItem = this.texts[i];
                this.context.font = textItem.size + "px Arial";
                this.context.fillStyle = textItem.color;
                this.context.fillText(textItem.text, textItem.x, textItem.y);

                // Seçili metnin etrafında çerçeve
                if (this.selectedTextId === textItem.id) {
                    const metrics = this.context.measureText(textItem.text);
                    const height = textItem.size;
                    this.context.strokeStyle = "#1890ff";
                    this.context.lineWidth = 1;
                    this.context.strokeRect(
                        textItem.x - 5,
                        textItem.y - height + 5,
                        metrics.width + 10,
                        height + 10
                    );
                }
            }
        },
        rotateLeft() {
            this.rotateCanvas(-90);
            this.saveState();
        },
        rotateRight() {
            this.rotateCanvas(90);
            this.saveState();
        },
        rotateCanvas(angle) {
            this.rotationAngle += angle;
            this.redrawAll();
        },
        removeImage(index) {
            this.images.splice(index, 1);
            if (this.images.length === 0) {
                this.$emit("close");
            } else if (index <= this.currentEditIndex) {
                this.currentEditIndex = Math.max(this.currentEditIndex - 1, 0);
                this.loadImageToCanvas(this.currentEditIndex);
            }
        },
        // Metni çift tıklamayla düzenleme
        onDblClick() {
            if (this.selectedTextId !== null) {
                this.editSelectedText();
            }
        },
        // Çift tıklanan metni düzenlemek için modal aç
        editSelectedText() {
            const selectedText = this.texts.find(t => t.id === this.selectedTextId);
            if (!selectedText) return;

            this.editingText = selectedText;
        },
        zoomIn() {
            this.zoomLevel += 0.1;
            this.applyZoom();
            this.saveState();
        },
        zoomOut() {
            if (this.zoomLevel > 0.1) {
                this.zoomLevel -= 0.1;
                this.applyZoom();
            }
            this.saveState();
        },
        applyZoom() {
            var canvas = this.$refs.editorCanvas;
            canvas.style.transformOrigin = "0 0";
            canvas.style.transform = "scale(" + this.zoomLevel + ")";
        },
        resetImage() {
            this.rotationAngle = 0;
            this.zoomLevel = 1;
            this.applyZoom();
            this.loadImageToCanvas(this.currentEditIndex);
        },
        saveState() {
            if (!this.isDrawing) {
                const canvas = this.$refs.editorCanvas;
                const dataURL = canvas.toDataURL();
                this.history = this.history.slice(0, this.currentHistoryIndex + 1);
                this.history.push(dataURL);
                this.currentHistoryIndex++;
            }
        },
        undo() {
            if (this.canUndo) {
                this.currentHistoryIndex--;
                this.loadStateFromHistory();
            }
        },
        redo() {
            if (this.canRedo) {
                this.currentHistoryIndex++;
                this.loadStateFromHistory();
            }
        },
        loadStateFromHistory() {
            const dataURL = this.history[this.currentHistoryIndex];
            const img = new Image();
            img.onload = () => {
                this.context.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
                this.context.drawImage(img, 0, 0);
            };
            img.src = dataURL;
        },
        selectColor(color) {
            this.drawColor = color;
            this.colorPickerVisible = false;
        },
        selectTextColor(color) {
            this.textColor = color;
            this.textColorPickerVisible = false;
        },
        handleKeyPress(event) {
            if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") {
                return;
            }

            const key = event.key.toUpperCase();

            if (event.ctrlKey) {
                switch (key) {
                    case "Z":
                        event.preventDefault();
                        this.undo();
                        break;
                    case "Y":
                        event.preventDefault();
                        this.redo();
                        break;
                }
                return;
            }

            switch (key) {
                case "B":
                    this.toggleDrawMode();
                    break;
                case "T":
                    this.toggleTextMode();
                    break;
                case "ARROWLEFT":
                    this.rotateLeft();
                    break;
                case "ARROWRIGHT":
                    this.rotateRight();
                    break;
                case "+":
                case "=":
                    this.zoomIn();
                    break;
                case "-":
                case "_":
                    this.zoomOut();
                    break;
                case "R":
                    this.resetImage();
                    break;
            }
        },
        getMousePos(e) {
            var mainCanvas = this.$refs.editorCanvas;
            if (!mainCanvas) return { x: 0, y: 0 };

            var rect = mainCanvas.getBoundingClientRect();
            var scaleX = mainCanvas.width / rect.width;
            var scaleY = mainCanvas.height / rect.height;

            var x = (e.clientX - rect.left) * scaleX;
            var y = (e.clientY - rect.top) * scaleY;

            return { x: x, y: y };
        },
        triggerAddImage() {
            this.$refs.addImageRef.value = null;
            this.$refs.addImageRef.click();
        },
        onNewImagesSelected(event) {
            var files = event.target.files;
            for (var i = 0; i < files.length; i++) {
                this.addImageToList(files[i]);
            }
        },
        addImageToList(file) {
            var self = this;
            var previewUrl = URL.createObjectURL(file);
            var tempImage = new Image();
            var imageData = {
                file: file,
                preview: previewUrl,
                caption: "",
                edited: null,
                originalWidth: 0,
                originalHeight: 0
            };
            tempImage.onload = function() {
                imageData.originalWidth = tempImage.width;
                imageData.originalHeight = tempImage.height;
                self.images.push(imageData);
            };
            tempImage.onerror = function(err) {
                console.error("Yeni resim yükleme hatası:", err);
                self.images.push(imageData);
            };
            tempImage.src = previewUrl;
        }
    },
    beforeDestroy() {
        for (let i = 0; i < this.images.length; i++) {
            URL.revokeObjectURL(this.images[i].preview);
        }
        window.removeEventListener("keydown", this.handleKeyPress);
    }
};
</script>

<style lang="scss">
.canvas-style {
    border: 1px solid #ccc;
    cursor: crosshair;

    &.text-mode {
        cursor: text;
    }

    &.dragging {
        cursor: move;
    }
}

.image-editor-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.image-editor-container {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 1000px;
    max-height: 90vh;
    height: 90vh;
    display: flex;
    flex-direction: column;
    position: relative;

    .header {
        padding: 16px;
        border-bottom: 1px solid #e8e8e8;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h3 {
            margin: 0;
        }

        .toolbar {
            display: flex;
            gap: 16px;
            align-items: center;
            margin: 0 24px;

            .tool-group {
                display: flex;
                align-items: flex-start;
                gap: 12px;

                .tool-wrapper {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                }

                .tool-btn {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 44px;
                    height: 44px;
                    padding: 0;

                    .material-icons {
                        font-size: 24px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 100%;
                        height: 100%;
                    }
                }

                .color-btn {
                    width: 36px !important;
                    height: 36px !important;
                    padding: 0;
                    border-radius: 50% !important;
                    border: 1px solid #d9d9d9;

                    &:hover {
                        border-color: #40a9ff;
                    }
                }

                .width-slider {
                    width: 100px;
                }

                .draw-options {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-left: 12px;

                    .color-btn {
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        margin-right: 8px;
                    }

                    .width-slider {
                        width: 100px;
                    }
                }
            }
        }
    }

    .content {
        padding: 16px;
        padding-bottom: 120px;
        overflow-y: auto;
        flex: 1;
        display: flex;
        flex-direction: column;

        .editor-container {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .caption-section {
            width: 100%;

            .caption-input {
                width: 100%;
                margin: 0 auto;
                display: block;
            }
        }

        .thumbnail-container {
            position: fixed;
            bottom: 64px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            flex-direction: column;
            gap: 8px;
            overflow-x: auto;
            padding: 8px 16px;
            background: white;
            border-top: 1px solid #e8e8e8;
            width: 90%;
            max-width: 1000px;
            z-index: 2;

            .thumbnail-list {
                display: flex;
                gap: 8px;
                overflow-x: auto;
                padding: 8px 16px;
            }

            .thumbnail-item {
                position: relative;
                border: 2px solid transparent;
                border-radius: 4px;
                cursor: pointer;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 4px;

                &.active {
                    border-color: #1890ff;
                }

                img {
                    display: block;
                    width: 80px;
                    height: 80px;
                    object-fit: cover;
                    border-radius: 4px;
                }

                .thumbnail-actions {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    opacity: 0;
                    transition: opacity 0.2s;
                }

                &:hover .thumbnail-actions {
                    opacity: 1;
                }

                &.add-new {
                    justify-content: center;
                    align-items: center;
                    min-width: 80px;
                    min-height: 80px;
                    border: 2px dashed #ccc;

                    i.material-icons {
                        font-size: 36px;
                        margin-bottom: 4px;
                    }
                }
            }
        }
    }

    .footer {
        padding: 16px;
        border-top: 1px solid #e8e8e8;
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        position: fixed;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        background: white;
        width: 90%;
        max-width: 1000px;
        z-index: 2;
    }
}

.color-picker {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 4px;
    padding: 8px;
    width: 280px;

    .color-option {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        cursor: pointer;
        border: 1px solid #e8e8e8;
        transition: transform 0.2s, border-color 0.2s;

        &:hover {
            transform: scale(1.1);
            border-color: #40a9ff;
        }
    }
}

.tool-btn {
    position: relative;
    padding-bottom: 20px;
    height: auto;

    .shortcut-hint {
        position: absolute;
        bottom: 2px;
        left: 50%;
        transform: translateX(-50%);
        font-size: 10px;
        color: #999;
    }
}

.color-btn {
    width: 36px !important;
    height: 36px !important;
    padding: 0;
    border-radius: 50% !important;
    border: 1px solid #d9d9d9;

    &:hover {
        border-color: #40a9ff;
    }
}

.draw-options {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: 12px;
}

.shortcut-hint {
    font-size: 11px;
    color: #999;
    text-align: center;
    line-height: 1;
    margin-top: 2px;
}

.delete-btn {
    width: 24px !important;
    height: 24px !important;
    display: flex;
    align-items: center;
    justify-content: center;

    .material-icons {
        font-size: 18px;
    }
}
</style>
